import * as Yup from 'yup';
import { useCallback, useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { Autocomplete, TextField, Checkbox, Chip } from '@mui/material';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
// types
import { HTTaiKhoan } from 'src/types/api-response';
// components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField, RHFSelect } from 'src/components/hook-form';
// hooks
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
import { PASSWORD_MSG, PASSWORD_REGEX } from 'src/constants';
import { useAccount } from './hooks'; // Import hook đã cập nhật

// ----------------------------------------------------------------------

type Props = {
  currentAccount?: HTTaiKhoan;
};

export default function AccountNewEditForm({ currentAccount }: Props) {
  const router = useRouter();
  const mdUp = useResponsive('up', 'md');
  const { enqueueSnackbar } = useSnackbar();

  // Lấy danh sách options từ hook useAccount
  const { updateAccount, roleOptions, statusOptions, fetchOptions } = useAccount();

  // Đảm bảo fetch options khi component mount (dù hook có useEffect nhưng gọi lại cho chắc chắn nếu cần refresh)
  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const isCreateMode = !currentAccount;

  // Schema Validation
  const AccountSchema = Yup.object().shape({
    tenTaiKhoan: Yup.string().required('Tên đăng nhập là bắt buộc'),
    email: Yup.string().required('Email là bắt buộc').email('Email không hợp lệ'),
    sdt: Yup.string().nullable(),
    trangThai: Yup.number().required('Trạng thái là bắt buộc'),
    maVaiTros: Yup.array().min(1, 'Phải chọn ít nhất 1 vai trò'),
    matKhau: Yup.string().test(
      'password-strength',
      PASSWORD_MSG, // Sử dụng thông báo chung
      (value) => {
        // 1. Edit mode -> Không check
        if (!isCreateMode) return true;

        // 2. Create mode nhưng để trống -> Dùng default 123456 (Pass)
        if (!value || value.length === 0) return true;

        // 3. Có nhập -> Phải đúng Regex chung
        return PASSWORD_REGEX.test(value);
      }
    ),
    // ----------------------------------

    confirmMatKhau: Yup.string().oneOf(
      [Yup.ref('matKhau'), undefined],
      'Mật khẩu xác nhận phải khớp'
    ),
  });

  // Default Values Logic
  const defaultValues = useMemo(() => {
    let currentRoles: any[] = [];

    if (currentAccount) {
      if (Array.isArray(currentAccount.maVaiTros) && currentAccount.maVaiTros.length > 0) {
        currentRoles = currentAccount.maVaiTros;
      } else if (currentAccount.htPhanQuyenTaiKhoans?.maVaiTro) {
        currentRoles = [currentAccount.htPhanQuyenTaiKhoans.maVaiTro];
      }
    }

    return {
      tenTaiKhoan: currentAccount?.tenTaiKhoan || '',
      email: currentAccount?.email || '',
      sdt: currentAccount?.sdt || '',
      trangThai: currentAccount?.trangThai ?? 1, // Default có thể là 1 hoặc lấy từ statusOptions[0]?.id
      maVaiTros: currentRoles,
      matKhau: '',
    };
  }, [currentAccount]);

  const methods = useForm({
    resolver: yupResolver(AccountSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (currentAccount) {
      reset(defaultValues);
    }
  }, [currentAccount, defaultValues, reset]);

  // Chuẩn bị options cho Autocomplete từ API
  const autocompleteRoleOptions = useMemo(
    () =>
      roleOptions.map((role) => ({
        label: role.text || '',
        value: role.value || '',
      })),
    [roleOptions]
  );

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        const { confirmMatKhau, ...restData } = data;

        if (isCreateMode) {
          const finalPassword = restData.matKhau ? restData.matKhau : '123456';
          const createPayload = { ...restData, matKhau: finalPassword };
          console.log('Payload tạo mới:', createPayload);
          enqueueSnackbar('Chức năng tạo mới đang phát triển (Đã log data)!', { variant: 'info' });
        } else {
          await updateAccount(currentAccount!.id!, {
            ...restData,
            matKhau: undefined,
          });
          enqueueSnackbar('Cập nhật tài khoản thành công!', { variant: 'success' });
        }
        router.push(paths.dashboard.account.list);
      } catch (error: any) {
        console.error('Error submitting account:', error);
        enqueueSnackbar(error.message || 'Có lỗi xảy ra, vui lòng thử lại!', {
          variant: 'error',
        });
      }
    },
    [isCreateMode, updateAccount, currentAccount, enqueueSnackbar, router]
  );

  const renderDetails = (
    <>
      {mdUp && (
        <Grid md={4}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Thông tin cá nhân
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Thiết lập thông tin đăng nhập, vai trò và trạng thái hoạt động...
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={8}>
        <Card>
          {!mdUp && <CardHeader title="Thông tin cá nhân" />}
          <Stack spacing={3} sx={{ p: 3 }}>
            <Box
              columnGap={2}
              rowGap={3}
              display="grid"
              gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
            >
              <RHFTextField name="tenTaiKhoan" label="Tên đăng nhập" disabled={!isCreateMode} />
              <RHFTextField name="email" label="Email" />
              <RHFTextField name="sdt" label="Số điện thoại" />

              {/* === Dùng Select ĐỘNG từ statusOptions === */}
              <RHFSelect name="trangThai" label="Trạng thái" InputLabelProps={{ shrink: true }}>
                {statusOptions.map((option) => (
                  <MenuItem
                    // Đảm bảo key không null, nếu value null thì dùng text hoặc index làm key dự phòng
                    key={option.value || option.text || Math.random()}
                    // Đảm bảo value không null, fallback về chuỗi rỗng hoặc 0 tùy logic của bạn
                    value={option.value ?? ''}
                  >
                    {/* Đảm bảo nội dung hiển thị không null */}
                    {option.text || ''}
                  </MenuItem>
                ))}
              </RHFSelect>
              {/* ======================================= */}
            </Box>

            {/* === Autocomplete ĐỘNG từ roleOptions === */}
            <Stack spacing={1}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={autocompleteRoleOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={autocompleteRoleOptions.filter((option) =>
                  (values.maVaiTros || []).some((selected: any) => {
                    const selectedId = typeof selected === 'object' ? selected?.id : selected;
                    return selectedId === option.value;
                  })
                )}
                onChange={(_, newValue) => {
                  setValue(
                    'maVaiTros',
                    newValue.map((item) => item.value),
                    { shouldValidate: true }
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vai trò"
                    placeholder="Chọn vai trò"
                    error={!!errors.maVaiTros}
                    helperText={errors.maVaiTros?.message as string}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox size="small" style={{ marginRight: 8 }} checked={selected} />
                    {option.label}
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.label}
                      size="small"
                      variant="soft"
                      color="primary"
                    />
                  ))
                }
              />
            </Stack>

            {isCreateMode && (
              <Box
                columnGap={2}
                rowGap={3}
                display="grid"
                gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
              >
                <RHFTextField name="matKhau" label="Mật khẩu" type="password" />
                <RHFTextField name="confirmMatKhau" label="Xác nhận mật khẩu" type="password" />
              </Box>
            )}
          </Stack>
        </Card>
      </Grid>
    </>
  );

  const renderActions = (
    <>
      {mdUp && <Grid md={4} />}
      <Grid xs={12} md={8} sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          color="inherit"
          variant="outlined"
          size="large"
          onClick={() => router.push(paths.dashboard.account.list)}
          sx={{ mr: 2 }}
        >
          Hủy
        </Button>
        <CustomLoadingButton type="submit" variant="contained" size="large" loading={isSubmitting}>
          {isCreateMode ? 'Tạo tài khoản' : 'Cập nhật'}
        </CustomLoadingButton>
      </Grid>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {renderDetails}
        {renderActions}
      </Grid>
    </FormProvider>
  );
}
